import type { Action, Dispatch } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';

import type ApiError from '../api/api-error';
import type { ErrorNotifier } from '../errors/error-notifier';
import type {
  ActionTypes,
  CreateArrayState,
  DataPayload,
  ErrorPayload,
  PayloadAction,
  UpdateArrayState,
} from '../types/state';

let errorNotifier: ErrorNotifier;

export const EXCLUDED_MESSAGES = [
  'Network request failed',
  'Email ou mot de passe incorrect',
  'Vous devez vous connecter ou vous enregistrer pour continuer',
  'Unknown error happened',
];

export const notifyError = (error: ApiError) => {
  if (errorNotifier) {
    if (!EXCLUDED_MESSAGES.includes(error.message)) errorNotifier.notify(error);
  } else {
    throw new Error('The error notifier needs to be initialized so that the onError callback can be used.');
  }
};

export const dispatchError = (dispatch: Dispatch, type: Action['type'], err: any) =>
  dispatch({
    type,
    payload: {
      message: err.message,
      status: err.status,
      errors: err.errors || [err.message],
      response: err.response,
    },
  });

/*
 * Packages that uses onError need to call this init method
 */
export const init = (notifier: ErrorNotifier) => {
  errorNotifier = notifier;
};

export const onSuccess =
  <T>(dispatch: Dispatch, type: Action['type']) =>
  (result: T) =>
    dispatch({ type, payload: result });

export const onError = (dispatch: Dispatch, type: Action['type']) => (err: any) => dispatchError(dispatch, type, err);

export const apiCall =
  (action: ActionTypes, apiCallback: () => void, resultCallback = (result: any) => result) =>
  async (dispatch: ThunkDispatch<any, void, Action>) => {
    dispatch({ type: action.START });
    try {
      const result = await apiCallback();
      return dispatch({ type: action.SUCCESS, payload: resultCallback(result) });
    } catch (error) {
      return dispatchError(dispatch, action.ERROR, error);
    }
  };

export const fetchInitialArrayState = {
  isLoading: false,
  isLoaded: false,
  errors: [],
  all: [],
};

export const updateInitialArrayState = {
  isUpdating: false,
  isUpdated: false,
  errors: [],
  all: [],
};

export const createInitialArrayState = {
  isCreating: false,
  isCreated: false,
  errors: [],
  all: [],
};

export const deleteInitialArrayState = {
  isDeleting: false,
  isDeleted: false,
  errors: [],
  all: [],
};

export const initialState = {
  ...fetchInitialArrayState,
  ...updateInitialArrayState,
  ...createInitialArrayState,
};

// TODO: add delete methods

const defaultMapper = (item: any) => item;

const fetchSuccess = <StateArray, ResponseItem, ResponseArray extends Array<ResponseItem>>(
  state: any,
  response: ResponseArray,
  mapper: (item: ResponseItem) => StateArray,
) => ({
  ...state,
  isLoading: false,
  isLoaded: true,
  all: response.map((item) => mapper(item)),
});

export const onFetchStart = (state: any) => ({
  ...state,
  isLoading: true,
  isLoaded: false,
  errors: [],
});

export type FetchSuccessWithMapperAction<T> = PayloadAction<DataPayload<T>> | PayloadAction<T>;

export const onFetchSuccessWithMapper =
  <StateArray, Item, ResponseArray extends Array<Item>>(mapper: (item: Item) => StateArray) =>
  (state: any, action: FetchSuccessWithMapperAction<ResponseArray>) =>
    fetchSuccess(state, 'data' in action.payload ? action.payload.data : action.payload, mapper);

export const onFetchSuccess = onFetchSuccessWithMapper(defaultMapper);

export const onFetchError = (state: any, action: PayloadAction<ErrorPayload>) => ({
  ...state,
  errors: action.payload.errors,
  isLoading: false,
});

export const onUpdateStart = (state: any) => ({
  ...state,
  isUpdated: false,
  isUpdating: true,
  errors: [],
});

export const onUpdateError = (state: any, action: PayloadAction<ErrorPayload>) => ({
  ...state,
  isUpdated: false,
  isUpdating: false,
  errors: action.payload.errors,
});

type UpdateSuccessWithMapperAction<T> = PayloadAction<DataPayload<T>>;

export const onUpdateSuccessWithMapper =
  <StateItem extends { id: any }, DataItem extends { id: any }>(mapper: (item: DataItem) => StateItem) =>
  (state: UpdateArrayState<StateItem>, { payload: { data } }: UpdateSuccessWithMapperAction<DataItem>) => ({
    ...state,
    isUpdated: true,
    isUpdating: false,
    all: state.all.map((item) => (item.id === data.id ? mapper(data) : item)),
  });

export const onUpdateSuccess = onUpdateSuccessWithMapper(defaultMapper);

export const onCreateStart = (state: any) => ({
  ...state,
  isCreated: false,
  isCreating: true,
  errors: [],
});

export const onCreateError = <T>(state: CreateArrayState<T>, action: PayloadAction<ErrorPayload>) => ({
  ...state,
  isCreated: false,
  isCreating: false,
  errors: action.payload.errors,
});

type CreateSuccessWithMapperAction<T> = PayloadAction<DataPayload<T>>;

export const onCreateSuccessWithMapper =
  <StateItem extends { id: any }, DataItem extends { id: any }>(mapper: (item: DataItem) => StateItem) =>
  (state: CreateArrayState<StateItem>, { payload: { data } }: CreateSuccessWithMapperAction<DataItem>) => ({
    ...state,
    isCreated: true,
    isCreating: false,
    all: [...state.all, mapper(data)],
  });

export const onCreateSuccess = onCreateSuccessWithMapper(defaultMapper);
